import React, { useEffect, useMemo, useState } from "react";
import InputBox from "../../../components/InputBox";
import { ThemeBasedColorHeader, ThemeBasedColorMenubar } from "src/shared/modules/profile/theme/themeCheacker";
import NumberPad from "src/business-types/grocery/layouts/Home/NumberPad";
import { Product } from "../NotesProducts";
import { CustomerData } from "src/shared/types";
import { useUser } from "src/shared/context/UserContext";

type PaymentMethod = "Credit" | "Card" | "Cash";
type OrderStatus = "Order Taken" | "Delivered";

interface PaymentProcessProps {
  customerDetail?: CustomerData | null;
  selectProductList: Product[];
  discount: { discountedAmount: string; discountValue: string; mode: string };
  receivedAmount: (amount: number, method: string) => void;
  clearData: string;
  throwStatus: (status: string) => void;
  finalAmount: number;
  resvedAmount?: number;
  setResvedAmount?: (data: number) => void;
  deliveryFee?: number;
  parcelFee?: number;
  deliveryFeeEnabled?: boolean;
  parcelFeeEnabled?: boolean;
  onDeliveryFeeChange?: (fee: number) => void;
  onParcelFeeChange?: (fee: number) => void;
}

const PaymentProcess: React.FC<PaymentProcessProps> = ({
  customerDetail,
  selectProductList,
  discount,
  receivedAmount,
  clearData,
  throwStatus,
  finalAmount,
  resvedAmount = 0,
  setResvedAmount,
  deliveryFee: initialDeliveryFee = 0,
  parcelFee: initialParcelFee = 0,
  deliveryFeeEnabled: propDeliveryFeeEnabled,
  parcelFeeEnabled: propParcelFeeEnabled,
  onDeliveryFeeChange,
  onParcelFeeChange,
}) => {
  const { userConfigration } = useUser();
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod>("Cash");
  const [amountReceived, setAmountReceived] = useState<string>("");
  const [orderStatus, setOrderStatus] = useState<OrderStatus>("Delivered");
  const [customer, setCustomer] = useState<CustomerData | null>();
  const [includeOldBalance, setIncludeOldBalance] = useState<boolean>(false);
  const [showPad, setShowPad] = useState(false);
  const [showDeliveryPad, setShowDeliveryPad] = useState(false);
  const [showParcelPad, setShowParcelPad] = useState(false);
  const [deliveryFee, setDeliveryFee] = useState<string>(String(initialDeliveryFee || 0));
  const [parcelFee, setParcelFee] = useState<string>(String(initialParcelFee || 0));

  // Check if fees are enabled - use prop or context
  const toBoolean = (value: any) => value === true || value === "true";
  const deliveryFeeEnabled = propDeliveryFeeEnabled !== undefined 
    ? toBoolean(propDeliveryFeeEnabled) 
    : toBoolean(userConfigration?.deliveryFeeEnabled);
  const parcelFeeEnabled = propParcelFeeEnabled !== undefined 
    ? toBoolean(propParcelFeeEnabled) 
    : toBoolean(userConfigration?.parcelFeeEnabled);

  useEffect(() => {
    setCustomer(customerDetail);
  }, [customerDetail]);

  // Update fees when props change
  useEffect(() => {
    if (initialDeliveryFee !== undefined) {
      setDeliveryFee(String(initialDeliveryFee));
    }
  }, [initialDeliveryFee]);

  useEffect(() => {
    if (initialParcelFee !== undefined) {
      setParcelFee(String(initialParcelFee));
    }
  }, [initialParcelFee]);

  // Notify parent about fee changes
  useEffect(() => {
    if (onDeliveryFeeChange) {
      onDeliveryFeeChange(Number(deliveryFee));
    }
  }, [deliveryFee, onDeliveryFeeChange]);

  useEffect(() => {
    if (onParcelFeeChange) {
      onParcelFeeChange(Number(parcelFee));
    }
  }, [parcelFee, onParcelFeeChange]);

  // 🔹 Compute total and final prices
  const isUnknownCustomer = customer?.firstName === "XXXX";

  const oldBalance = useMemo(() => Number(customer?.totalOutstandingBalance || 0), [customer?.totalOutstandingBalance]);
  
  // Total payable - finalAmount already includes fees from parent
  // We just need to add outstanding balance if included
  const totalPayable = useMemo(() => {
    return includeOldBalance ? finalAmount + oldBalance : finalAmount;
  }, [finalAmount, oldBalance, includeOldBalance]);
  
  const receivedNum = Number(amountReceived || 0);

  const balance = useMemo(() => {
    if (receivedNum < totalPayable) {
      return totalPayable - receivedNum; // Remaining balance
    } else {
      return 0;
    }
  }, [totalPayable, receivedNum]);

  const returnAmount = useMemo(() => {
    if (receivedNum > totalPayable) {
      return receivedNum - totalPayable; // Change to give back
    } else {
      return 0;
    }
  }, [totalPayable, receivedNum]);

  // 🔹 Payment options (disable Credit for guest)
  const paymentOptions = useMemo<PaymentMethod[]>(
    () =>
      customerDetail?.firstName === "XXXX"
        ? ["Card", "Cash"]
        : Number(customer?.totalOutstandingBalance) + finalAmount < 0
          ? ["Credit"]
          : ["Credit", "Card", "Cash"],
    [customerDetail?.firstName, customer?.totalOutstandingBalance, finalAmount],
  );

  //------- Input Disabled Logic ---//
  const inputDisabled = selectedMethod === "Credit" || selectedMethod === "Card" || totalPayable <= 0;

  const isUnderPaidForUnknown = isUnknownCustomer && receivedNum < totalPayable;

  // 🔹 Sync amount & method
  useEffect(() => {
    receivedAmount(Number(amountReceived || 0), selectedMethod);
  }, [amountReceived, selectedMethod, receivedAmount]);

  // 🔹 Reset on clear
  useEffect(() => {
    setAmountReceived("");
    setDeliveryFee(String(initialDeliveryFee || 0));
    setParcelFee(String(initialParcelFee || 0));
    if (Number(customer?.totalOutstandingBalance) < 0) {
      setIncludeOldBalance(true)
    } else {
      setIncludeOldBalance(false)
    }
  }, [clearData, customer?.totalOutstandingBalance, initialDeliveryFee, initialParcelFee]);

  useEffect(() => {
    if (selectedMethod === "Card") {
      setAmountReceived(totalPayable.toFixed(2));
    } else if (selectedMethod !== "Cash") {
      setAmountReceived("");
    }
  }, [selectedMethod, totalPayable]);

  // 🔹 Status dropdown
  const renderDropdown = (value: OrderStatus, title: string) => {
    const statusOptions: OrderStatus[] = ["Order Taken", "Delivered"];
    const canChangeStatus = customerDetail?.firstName && customerDetail.firstName !== "XXXX";

    return (
      <div className='flex justify-between items-center text-lg py-1'>
        <p className='font-medium w-1/2 text-start'>{title}</p>:
        <div className='w-1/2 m-0.5 text-start'>
          <select
            value={value}
            onChange={(e) => {
              const newStatus = e.target.value as OrderStatus;
              setOrderStatus(newStatus);
              throwStatus(newStatus);
            }}
            disabled={!canChangeStatus}
            className='w-full px-2 py-1 border border-gray-300 rounded-md bg-white text-gray-700 
                       focus:ring-2 focus:ring-green-400 focus:outline-none'
          >
            {statusOptions.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  const hasProducts = selectProductList.length >= 0;

  const shouldShowBalanceDue = hasProducts && totalPayable > 0 && receivedNum < totalPayable;

  const shouldShowChange = hasProducts && totalPayable > 0 && receivedNum > totalPayable;

  const paymentAmount = () => {
    if (receivedNum >= totalPayable) {
      return totalPayable;
    } else {
      return receivedNum;
    }
  };

  // Helper function to handle number pad input
  const handleNumberPadChange = (
    val: string | number,
    setter: React.Dispatch<React.SetStateAction<string>>
  ) => {
    const valStr = String(val);
    if (/^\d*\.?\d{0,2}$/.test(valStr)) {
      setter(Number(valStr).toFixed(2));
    }
  };

  return (
    <div className='w-full p-2 bg-white border border-dashed border-black rounded-lg shadow-md'>
      <h1 className={`text-xl font-bold border px-2 py-1 rounded-lg text-left ${ThemeBasedColorHeader()}`}>Payment Process</h1>

      {/* Payment buttons */}
      <div className='flex flex-wrap justify-center gap-2 sm:gap-3 mt-2'>
        {paymentOptions.map((option) => {
          const isSelected = selectedMethod === option;

          return (
            <button
              key={option}
              onClick={() => setSelectedMethod(option)}
              className={`
          px-3 py-1 sm:px-4 sm:py-1 
          rounded-md border text-sm sm:text-base 
          min-w-[60px] sm:min-w-[80px]
          transition-all duration-200

          ${isSelected ? `font-semibold ${ThemeBasedColorHeader()}` : `hover:bg-gray-200 ${ThemeBasedColorMenubar()}`}
        `}
            >
              {option}
            </button>
          );
        })}
      </div>

      {/* Payment Details */}
      <div className='mt-3 space-y-2 bg-gray-50 rounded-2xl shadow-inner p-4 border border-gray-200'>
        {/* Order Status */}
        {renderDropdown(orderStatus, "Status")}

        {/* Outstanding Balance */}
        <div className='flex justify-between items-center text-base py-1'>
          <div className='flex items-center gap-2'>
            <input
              type='checkbox'
              checked={includeOldBalance}
              onChange={(e) => setIncludeOldBalance(e.target.checked)}
              disabled={oldBalance < 0}
              className='w-4 h-4 accent-blue-600'
            />
            <p className='font-medium text-gray-700'>Outstanding Balance:</p>
          </div>
          <p className='font-semibold text-gray-800'>{oldBalance.toFixed(2)} QAR</p>
        </div>
        <div className="border-t p-1">

        {/* Delivery Fee - Conditionally Rendered */}
        {deliveryFeeEnabled && (
          <div className='flex justify-between items-center text-base py-1'>
            <p className='font-medium text-gray-700'>Delivery fee:</p>
            <div className='w-1/2 flex justify-end'>
              <InputBox
                className='w-full text-right placeholder:text-right font-semibold'
                placeholder='0.00'
                type='string'
                value={deliveryFee}
                onClick={() => {
                  setShowDeliveryPad(true);
                }}
                disabled={totalPayable <= 0}
                onlyNumber={true}
                readOnly={true}
              />
              <p className='font-bold pl-1'> QAR</p>
            </div>
          </div>
        )}

        {/* Parcel Fee - Conditionally Rendered */}
        {parcelFeeEnabled && (
          <div className='flex justify-between items-center text-base py-1'>
            <p className='font-medium text-gray-700'>Parcel Fee:</p>
            <div className='w-1/2 flex justify-end'>
              <InputBox
                className='w-full text-right placeholder:text-right font-semibold'
                placeholder='0.00'
                type='string'
                value={parcelFee}
                onClick={() => {
                  setShowParcelPad(true);
                }}
                disabled={totalPayable <= 0}
                onlyNumber={true}
                readOnly={true}
              />
              <p className='font-bold pl-1'> QAR</p>
            </div>
          </div>
        )}
        </div>

        {/* Total Payable - Now shows correct amount without double-counting fees */}
        <div className='flex justify-between items-center border-t pt-2'>
          <p className='font-semibold text-gray-800'>Total Payable:</p>
          <p className='font-bold text-lg text-gray-900'>{totalPayable.toFixed(2)} QAR</p>
        </div>

        {includeOldBalance && <p className='text-xs text-gray-500 text-right'>(Includes outstanding balance)</p>}

        {/* Amount Paid */}
        <div className='flex justify-between items-center text-base py-1'>
          <p className='font-medium text-gray-700'>Received Amount:</p>
          <div className='w-1/2 flex justify-end'>
            <InputBox
              className='w-full text-right placeholder:text-right font-semibold'
              placeholder='0.00'
              type='string'
              value={amountReceived}
              onClick={() => {
                setShowPad(true);
              }}
              disabled={inputDisabled}
              onlyNumber={true}
              readOnly={true}
            />
            <p className='font-bold pl-1'> QAR</p>
          </div>
        </div>

        {/* Number Pads */}
        {showPad && (
          <NumberPad
            value={amountReceived || ""}
            onChange={(val) => handleNumberPadChange(val, setAmountReceived)}
            onClose={() => {
              setShowPad(false);
            }}
          />
        )}

        {deliveryFeeEnabled && showDeliveryPad && (
          <NumberPad
            value={deliveryFee || ""}
            onChange={(val) => handleNumberPadChange(val, setDeliveryFee)}
            onClose={() => {
              setShowDeliveryPad(false);
            }}
          />
        )}

        {parcelFeeEnabled && showParcelPad && (
          <NumberPad
            value={parcelFee || ""}
            onChange={(val) => handleNumberPadChange(val, setParcelFee)}
            onClose={() => {
              setShowParcelPad(false);
            }}
          />
        )}

        {isUnderPaidForUnknown && <p className='text-red-500 text-xs text-right font-medium'>Full payment required for walk-in customer</p>}

        {/* Payment Applied */}
        <div className='flex justify-between items-center pt-2 border-t'>
          <p className='font-medium text-gray-700'>Payment Applied:</p>
          <p className='font-bold text-lg text-blue-600'>{paymentAmount().toFixed(2)} QAR</p>
        </div>

        {/* Balance Due (Always evaluated, but conditionally shown) */}
        {shouldShowBalanceDue && (
          <div className='flex justify-between items-center py-2 border-t mt-2'>
            <p className='font-semibold text-red-600'>Balance Due:</p>
            <p className='font-bold text-xl text-red-600'>{balance.toFixed(2)} QAR</p>
          </div>
        )}

        {/* Change */}
        {shouldShowChange && (
          <div className='flex justify-between items-center py-2 border-t mt-2'>
            <p className='font-semibold text-green-600'>Change:</p>
            <p className='font-bold text-xl text-green-600'>{returnAmount.toFixed(2)} QAR</p>
          </div>
        )}
      </div>

      {selectedMethod === "Credit" && (
        <div className='mt-4 p-3 bg-blue-50 rounded-xl shadow-inner border border-blue-200'>
          <h2 className='text-lg font-semibold text-blue-700 mb-2'>Credit Payment Summary</h2>
          <p className='text-gray-700'>
            You have chosen <span className='font-semibold text-blue-600'>Credit</span> as your payment method.
          </p>
          <div className='mt-2 p-2 bg-white rounded-lg'>
            <div className='flex justify-between'>
              <p className='font-medium'>Amount to be credited:</p>
              <p className='font-bold text-blue-600'>{totalPayable.toFixed(2)} QAR</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(PaymentProcess);